import { isBlobNamespace } from '../../namespaces.js';
import { getLocalBlob, putLocalBlob } from './local-blob.js';
import { parseRecordKey, recordKey, recordPrefix, } from './local-key-encoding.js';
import { migrateLegacyLocalStorage } from './local-migration.js';
export class LocalStorageProvider {
    id = 'local';
    displayName = 'This device';
    now;
    constructor(opts = {}) {
        this.now = opts.now ?? (() => new Date().toISOString());
        if (opts.shouldMigrate !== false) {
            migrateLegacyLocalStorage(this.now);
        }
    }
    async isReady() {
        return typeof localStorage !== 'undefined';
    }
    async getRecord(ns, key) {
        if (typeof localStorage === 'undefined')
            return null;
        const raw = localStorage.getItem(recordKey(ns, key));
        if (!raw)
            return null;
        try {
            const parsed = JSON.parse(raw);
            // Defensive: strip sidecars in case a legacy write left corrupt blobs ({}).
            if (parsed.sidecars)
                delete parsed.sidecars;
            return parsed;
        }
        catch {
            return null;
        }
    }
    async putRecord(rec) {
        if (typeof localStorage === 'undefined')
            return;
        // Strip sidecars (Blob objects) before serializing — JSON.stringify turns
        // them into `{}` which would later corrupt repair-driven re-uploads. Sidecars
        // are derived data (PDFs from JSON); they're regenerated on next save.
        const { sidecars: _ignored, ...persisted } = rec;
        localStorage.setItem(recordKey(rec.namespace, rec.key), JSON.stringify(persisted));
    }
    async deleteRecord(ns, key, deletedAt) {
        if (typeof localStorage === 'undefined')
            return;
        const existing = await this.getRecord(ns, key);
        const tombstone = {
            namespace: ns,
            key,
            value: existing?.value ?? null,
            updatedAt: existing?.updatedAt ?? deletedAt,
            deletedAt,
            version: existing?.version,
        };
        localStorage.setItem(recordKey(ns, key), JSON.stringify(tombstone));
    }
    async listRefs(ns) {
        if (typeof localStorage === 'undefined')
            return [];
        const prefix = recordPrefix(ns);
        const refs = [];
        for (let i = 0; i < localStorage.length; i++) {
            const storageKey = localStorage.key(i);
            if (!storageKey || !storageKey.startsWith(prefix))
                continue;
            const parsed = parseRecordKey(storageKey);
            if (!parsed || parsed.namespace !== ns)
                continue;
            const raw = localStorage.getItem(storageKey);
            if (!raw)
                continue;
            try {
                const env = JSON.parse(raw);
                refs.push({
                    namespace: env.namespace,
                    key: env.key,
                    updatedAt: env.updatedAt,
                    deletedAt: env.deletedAt,
                });
            }
            catch {
                // skip corrupt entries
            }
        }
        return refs;
    }
    async getBlob(ns, key) {
        if (!isBlobNamespace(ns))
            return null;
        try {
            return await getLocalBlob(ns, key);
        }
        catch {
            return null;
        }
    }
    async putBlob(ns, key, blob, updatedAt) {
        if (!isBlobNamespace(ns))
            return;
        await putLocalBlob(ns, key, blob, updatedAt);
    }
    /**
     * Local provider doesn't persist sidecars — they're derived data (e.g. PDFs)
     * and can be regenerated on demand from the JSON envelope.
     */
    async getSidecar() {
        return null;
    }
}
//# sourceMappingURL=local-provider.js.map